import classNames from "classnames";

import { ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";

interface DrawerProps {
  id?: string;
  className?: string;
  width?: number | string;
  title?: string | ReactNode;
  subtitle?: string | ReactNode;
  opened: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  position?: "left" | "right";
}

export const Drawer = ({
  id,
  className,
  width = 480,
  title,
  subtitle,
  opened,
  onClose,
  children,
  footer,
  position = "right",
}: DrawerProps) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && opened) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    if (opened) document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [opened, onClose]);

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <>
      {opened && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[100] bg-primary-900/20 backdrop-blur-sm transition-opacity duration-200 dark:bg-content-950/70"
            onClick={onClose}
          />

          {/* Panel */}
          <div
            id={id}
            className={classNames(
              "fixed top-0 bottom-0 z-[110] flex flex-col",
              "w-full",
              position === "right" ? "right-0" : "left-0",
              // Liquid glass
              "bg-white/75 dark:bg-content-900/90",
              "backdrop-blur-2xl",
              "shadow-[0_20px_60px_-20px_rgba(15,23,42,0.45)] dark:shadow-[0_20px_60px_-20px_rgba(0,0,0,0.7)]",
              position === "right"
                ? "border-l border-solid border-white/30 dark:border-primary-800/50"
                : "border-r border-solid border-white/30 dark:border-primary-800/50",
              "font-sans",
              className,
            )}
            style={{ maxWidth: width }}
          >
            {/* Glass shimmer overlay */}
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(130%_90%_at_8%_0%,rgba(255,255,255,0.55)_0%,rgba(255,255,255,0.12)_45%,rgba(255,255,255,0.03)_100%)] dark:bg-[radial-gradient(130%_90%_at_8%_0%,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.015)_45%,transparent_100%)]" />

            {/* Header */}
            {(title || subtitle) && (
              <div className="relative flex flex-col px-6 py-4 border-b border-white/30 dark:border-primary-800/50">
                <div className="flex items-center gap-3">
                  {title && (
                    <h2 className="font-header text-xl font-semibold text-primary-900 dark:text-primary-100">
                      {title}
                    </h2>
                  )}
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close panel"
                    className="ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-white/25 bg-white/30 text-content-600 shadow-sm backdrop-blur transition-colors hover:bg-white/50 dark:border-content-700/45 dark:bg-content-900/35 dark:text-content-300 dark:hover:bg-content-900/55"
                  >
                    <div className="icon-[ph--x] h-4 w-4" />
                  </button>
                </div>
                {subtitle && (
                  <p className="mt-1 font-sans text-sm text-content-500 dark:text-content-400">
                    {subtitle}
                  </p>
                )}
              </div>
            )}

            {/* Scrollable content */}
            <div className="relative flex-1 overflow-y-auto overscroll-contain p-6">
              {children}
            </div>

            {/* Footer */}
            {footer && (
              <div className="relative border-t border-white/30 dark:border-primary-800/50 bg-white/20 dark:bg-content-900/20 px-6 py-4">
                {footer}
              </div>
            )}
          </div>
        </>
      )}
    </>,
    document.body,
  );
};

export default Drawer;
